/**
 * DialogManager - Sistema Genérico para Gerenciamento de Dialogs
 * @description Gerencia dialogs HTML5 com diferentes posições, animações e comportamentos
 * @version 2.0.0
 * @author Irroba Platform
 */

(function(window, document) {
    'use strict';

    const DialogManager = {
        /**
         * Configurações padrão para todos os dialogs
         */
        defaults: {
            position: 'center',           // 'center', 'left', 'right'
            animation: 'fade',            // 'fade', 'slide'
            animationDuration: 300,       // Duração da animação em ms
            backdrop: true,               // Mostrar backdrop (fundo escuro)
            backdropClass: 'dialog-backdrop',
            closeOnBackdrop: true,        // Fechar ao clicar no backdrop
            closeOnEscape: true,          // Fechar ao pressionar ESC
            onOpen: null,                 // Callback ao abrir
            onClose: null,                // Callback ao fechar
            beforeOpen: null,             // Callback antes de abrir
            beforeClose: null             // Callback antes de fechar
        },

        /**
         * Armazena referências dos dialogs ativos
         */
        activeDialogs: [],

        /**
         * Inicializa um dialog com configurações customizadas
         * @param {string|HTMLElement} dialogElement - Seletor CSS ou elemento do dialog
         * @param {Object} options - Opções de configuração
         * @returns {Object} - API pública do dialog
         */
        init: function(dialogElement, options = {}) {
            const dialog = typeof dialogElement === 'string' 
                ? document.querySelector(dialogElement) 
                : dialogElement;

            if (!dialog) {
                console.error('[DialogManager] Dialog element not found:', dialogElement);
                return null;
            }

            if (dialog.nodeName !== 'DIALOG') {
                console.error('[DialogManager] Element is not a <dialog>:', dialog);
                return null;
            }

            // Verifica se já foi inicializado
            if (dialog.dialogManager) {
                console.warn('[DialogManager] Dialog already initialized:', dialog);
                return dialog.dialogManager;
            }

            // Mescla configurações
            const config = { ...this.defaults, ...options };
            dialog.dialogConfig = config;

            // Configura o dialog
            this.setupDialog(dialog);
            this.attachEvents(dialog);

            // Cria API pública
            const api = {
                open: () => this.open(dialog),
                close: () => this.close(dialog),
                toggle: () => this.toggle(dialog),
                destroy: () => this.destroy(dialog),
                isOpen: () => dialog.open
            };

            dialog.dialogManager = api;

            return api;
        },

        /**
         * Configura as classes e estilos iniciais do dialog
         * @param {HTMLDialogElement} dialog
         */
        setupDialog: function(dialog) {
            const config = dialog.dialogConfig;

            // Adiciona classes de gerenciamento
            if (!dialog.classList.contains('dialog-managed')) {
                dialog.classList.add('dialog-managed');
                dialog.classList.add(`dialog-position-${config.position}`);
                dialog.classList.add(`dialog-animation-${config.animation}`);
            }

            // Define variável CSS para duração da animação
            dialog.style.setProperty('--dialog-duration', `${config.animationDuration}ms`);

            // Previne exibição antes da inicialização (evita flash)
            dialog.style.visibility = 'visible';
        },

        /**
         * Anexa todos os event listeners necessários
         * @param {HTMLDialogElement} dialog
         */
        attachEvents: function(dialog) {
            const config = dialog.dialogConfig;

            // Botões de fechar dentro do dialog
            const closeButtons = dialog.querySelectorAll('[data-dialog-close]');
            closeButtons.forEach(button => {
                button.addEventListener('click', (e) => {
                    e.preventDefault();
                    this.close(dialog);
                });
            });

            // Fechar ao clicar no backdrop (área externa)
            if (config.closeOnBackdrop) {
                dialog.addEventListener('click', (e) => {
                    if (e.target === dialog) {
                        this.close(dialog);
                    }
                });
            }

            // Fechar ao pressionar ESC
            if (config.closeOnEscape) {
                dialog.addEventListener('keydown', (e) => {
                    if (e.key === 'Escape' && this.isTopDialog(dialog)) {
                        e.preventDefault();
                        this.close(dialog);
                    }
                });
            }

            // Previne fechamento acidental ao pressionar ESC
            dialog.addEventListener('cancel', (e) => {
                if (!config.closeOnEscape) {
                    e.preventDefault();
                }
            });
        },

        /**
         * Abre o dialog
         * @param {HTMLDialogElement} dialog
         */
        open: function(dialog) {
            if (!dialog || dialog.open) return;

            const config = dialog.dialogConfig;

            // Callback antes de abrir
            if (config.beforeOpen && typeof config.beforeOpen === 'function') {
                const shouldContinue = config.beforeOpen(dialog);
                if (shouldContinue === false) return;
            }

            // Cria backdrop se necessário
            if (config.backdrop) {
                this.createBackdrop(dialog);
            }

            // Bloqueia scroll do body
            this.lockScroll();

            // Inicia animação de abertura
            dialog.classList.add('dialog-opening');
            
            // Abre o dialog nativo
            dialog.showModal();

            // Força reflow para garantir animação
            dialog.offsetHeight;

            // Adiciona classe de dialog aberto
            requestAnimationFrame(() => {
                dialog.classList.add('dialog-open');
                dialog.classList.remove('dialog-opening');
            });

            // Adiciona à lista de dialogs ativos
            this.activeDialogs.push(dialog);

            // Callback após abertura
            if (config.onOpen && typeof config.onOpen === 'function') {
                setTimeout(() => config.onOpen(dialog), config.animationDuration);
            }
        },

        /**
         * Fecha o dialog
         * @param {HTMLDialogElement} dialog
         */
        close: function(dialog) {
            if (!dialog || !dialog.open) return;

            const config = dialog.dialogConfig;

            // Callback antes de fechar
            if (config.beforeClose && typeof config.beforeClose === 'function') {
                const shouldContinue = config.beforeClose(dialog);
                if (shouldContinue === false) return;
            }

            // Adiciona classe de fechamento
            dialog.classList.add('dialog-closing');
            dialog.classList.remove('dialog-open');

            // Aguarda animação e fecha
            setTimeout(() => {
                dialog.close();
                dialog.classList.remove('dialog-closing');

                // Remove backdrop
                this.removeBackdrop(dialog);

                // Remove da lista de ativos
                this.activeDialogs = this.activeDialogs.filter(d => d !== dialog);

                // Desbloqueia scroll se não houver mais dialogs
                if (this.activeDialogs.length === 0) {
                    this.unlockScroll();
                }

                // Callback após fechamento
                if (config.onClose && typeof config.onClose === 'function') {
                    config.onClose(dialog);
                }
            }, config.animationDuration);
        },

        /**
         * Alterna o estado do dialog (abre/fecha)
         * @param {HTMLDialogElement} dialog
         */
        toggle: function(dialog) {
            if (dialog.open) {
                this.close(dialog);
            } else {
                this.open(dialog);
            }
        },

        /**
         * Destrói o dialog e remove todos os listeners
         * @param {HTMLDialogElement} dialog
         */
        destroy: function(dialog) {
            if (dialog.open) {
                this.close(dialog);
            }
            
            delete dialog.dialogConfig;
            delete dialog.dialogManager;
            dialog.classList.remove('dialog-managed');
            dialog.style.removeProperty('--dialog-duration');
        },

        /**
         * Cria e exibe o backdrop (fundo escuro)
         * @param {HTMLDialogElement} dialog
         */
        createBackdrop: function(dialog) {
            const config = dialog.dialogConfig;
            
            // Remove backdrop existente
            this.removeBackdrop();

            const backdrop = document.createElement('div');
            backdrop.className = `${config.backdropClass} dialog-backdrop-${config.position}`;
            backdrop.setAttribute('data-dialog-backdrop', 'true');

            document.body.appendChild(backdrop);

            // Força reflow para animação
            backdrop.offsetHeight;

            // Ativa backdrop com animação
            requestAnimationFrame(() => {
                backdrop.classList.add('active');
            });

            // Fecha dialog ao clicar no backdrop
            if (config.closeOnBackdrop) {
                backdrop.addEventListener('click', () => this.close(dialog));
            }
        },

        /**
         * Remove o backdrop do DOM
         */
        removeBackdrop: function() {
            const backdrop = document.querySelector('[data-dialog-backdrop]');
            if (backdrop) {
                backdrop.classList.remove('active');
                setTimeout(() => backdrop.remove(), 300);
            }
        },

        /**
         * Bloqueia o scroll do body quando dialog está aberto
         */
        lockScroll: function() {
            if (this.activeDialogs.length === 0) {
                const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
                document.body.style.overflow = 'hidden';
                if (scrollbarWidth > 0) {
                    document.body.style.paddingRight = `${scrollbarWidth}px`;
                }
            }
        },

        /**
         * Desbloqueia o scroll do body
         */
        unlockScroll: function() {
            document.body.style.overflow = '';
            document.body.style.paddingRight = '';
        },

        /**
         * Verifica se o dialog é o que está no topo da pilha
         * @param {HTMLDialogElement} dialog
         * @returns {boolean}
         */
        isTopDialog: function(dialog) {
            return this.activeDialogs[this.activeDialogs.length - 1] === dialog;
        },

        /**
         * Fecha todos os dialogs ativos
         */
        closeAll: function() {
            [...this.activeDialogs].forEach(dialog => this.close(dialog));
        }
    };

    /**
     * Auto-inicialização via data-attributes
     * Detecta e inicializa dialogs automaticamente no DOM
     */
    DialogManager.autoInit = function() {
        // Dialogs com toggle (abre/fecha)
        document.querySelectorAll('[data-dialog-toggle]').forEach(trigger => {
            if (trigger.hasAttribute('data-dialog-initialized')) return;

            const targetSelector = trigger.getAttribute('data-dialog-toggle');
            const dialogElement = document.querySelector(targetSelector);

            if (!dialogElement) {
                console.warn('[DialogManager] Dialog not found for toggle:', targetSelector);
                return;
            }

            const options = {
                position: trigger.getAttribute('data-dialog-position') || 'center',
                animation: trigger.getAttribute('data-dialog-animation') || 'fade',
                closeOnBackdrop: trigger.getAttribute('data-dialog-close-backdrop') !== 'false',
                closeOnEscape: trigger.getAttribute('data-dialog-close-escape') !== 'false'
            };

            const dialogInstance = DialogManager.init(dialogElement, options);

            trigger.addEventListener('click', function(e) {
                e.preventDefault();
                dialogInstance.toggle();
            });

            trigger.setAttribute('data-dialog-initialized', 'true');
        });

        // Dialogs com open (apenas abre)
        document.querySelectorAll('[data-dialog-open]').forEach(trigger => {
            if (trigger.hasAttribute('data-dialog-initialized')) return;

            const targetSelector = trigger.getAttribute('data-dialog-open');
            const dialogElement = document.querySelector(targetSelector);

            if (!dialogElement) {
                console.warn('[DialogManager] Dialog not found for open:', targetSelector);
                return;
            }

            const options = {
                position: trigger.getAttribute('data-dialog-position') || 'center',
                animation: trigger.getAttribute('data-dialog-animation') || 'fade',
                closeOnBackdrop: trigger.getAttribute('data-dialog-close-backdrop') !== 'false',
                closeOnEscape: trigger.getAttribute('data-dialog-close-escape') !== 'false'
            };

            const dialogInstance = DialogManager.init(dialogElement, options);

            trigger.addEventListener('click', function(e) {
                e.preventDefault();
                dialogInstance.open();
            });

            trigger.setAttribute('data-dialog-initialized', 'true');
        });

        // Botões de fechar fora do dialog (se necessário)
        document.querySelectorAll('[data-dialog-close-target]').forEach(closeBtn => {
            if (closeBtn.hasAttribute('data-dialog-initialized')) return;

            const targetSelector = closeBtn.getAttribute('data-dialog-close-target');
            const dialogElement = document.querySelector(targetSelector);

            if (dialogElement && dialogElement.dialogManager) {
                closeBtn.addEventListener('click', function(e) {
                    e.preventDefault();
                    dialogElement.dialogManager.close();
                });
                closeBtn.setAttribute('data-dialog-initialized', 'true');
            }
        });
    };

    /**
     * Inicialização automática quando o DOM estiver pronto
     */
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
            DialogManager.autoInit();
        });
    } else {
        // DOM já está pronto
        DialogManager.autoInit();
    }

    // Expõe DialogManager globalmente
    window.DialogManager = DialogManager;

})(window, document);

// Export para ES Modules
export default window.DialogManager;
